import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from '../api/axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { FaPhoneAlt, FaEnvelope, FaGlobe, FaMapMarkerAlt, FaTrain, FaChevronDown, FaChevronUp, FaHeart, FaCheck, FaCalculator } from 'react-icons/fa';
import RequestInfoCard from '../components/RequestInfoCard';
import { useChoiceList } from '../context/ChoiceListContext';
import { ALLOTMENT_YEAR, CUTOFF_START_YEAR, CUTOFF_END_YEAR } from '../constants/years';
import { useLeadContext } from '../context/LeadContext';
import { JsonLd } from '../components/SEO/JsonLd';
import { buildCollegeSchema, buildCutoffDatasetSchema, buildFAQSchema, buildBreadcrumbSchema } from '../utils/seo';
import { buildCollegeOverview } from '../utils/collegeOverview';

interface District { id: number; name: string; state: string; }

interface College {
  id: number; college_code: string; college_name: string; college_type: string;
  address: string; district: District; pincode?: string; phone?: string;
  email?: string; website?: string; naac_grade?: string; autonomous_status?: boolean;
  womens_college?: boolean; minority_institution?: boolean;
  boys_accommodation_available?: boolean; boys_permanent_or_rental?: string;
  boys_mess_type?: string; boys_room_rent?: number; boys_electricity_charges?: number;
  girls_accommodation_available?: boolean; girls_permanent_or_rental?: string;
  girls_mess_type?: string; girls_room_rent?: number; girls_electricity_charges?: number;
  caution_deposit?: number; establishment_charges?: number; establishment_charges_period?: 'semester' | 'annual' | null; admission_fees?: number;
  annual_tuition_fee?: number; development_fee?: number;
  transport_facilities?: boolean; min_transport_charges?: number; max_transport_charges?: number;
  placement_info?: string; distance_from_district_hq?: number;
  nearest_railway_station?: string; distance_from_railway?: number;
  latitude?: number; longitude?: number;
}

interface CommunityAllocation { id: number; community: string; total_seats: number; seats_allotted: number; year: number; }

interface Course {
  id: number; branch_code: string; branch_name: string; degree_type: string;
  approved_intake_2025: number; total_seats: number; course_start_year?: number;
  nba_accredited?: boolean; accreditation_valid_upto?: number; is_active?: boolean;
  community_allocations?: CommunityAllocation[];
}

const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
  <section id={id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
    <h2 className="text-lg font-bold text-gray-800 mb-5 pb-3 border-b border-gray-100">{title}</h2>
    {children}
  </section>
);

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex justify-between items-center py-2.5 border-b border-gray-50 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-semibold text-gray-800 text-right">{value}</span>
  </div>
);

const COMMUNITIES = ['OC', 'BC', 'BCM', 'MBC', 'SC', 'SCA', 'ST'];

const CollegeDetails = () => {
  const { code } = useParams();
  const { setLeadContext } = useLeadContext();
  const { addToChoiceList, isInChoiceList } = useChoiceList();
  const [college, setCollege] = useState<College | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCourseId, setExpandedCourseId] = useState<number | null>(null);
  const [loadingAllocations, setLoadingAllocations] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState('overview');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // Trend chart state
  const [trendCommunity, setTrendCommunity] = useState('OC');
  const [trendData, setTrendData] = useState<Record<number, any[]>>({}); // courseId → years[]
  const [loadingTrend, setLoadingTrend] = useState<number | null>(null);

  // Similar colleges state
  const [similarColleges, setSimilarColleges] = useState<any[]>([]);

  // Fee calculator state
  const [feeYears, setFeeYears] = useState(4);
  const [feeHostel, setFeeHostel] = useState<'none' | 'boys' | 'girls'>('none');
  const [feeTransport, setFeeTransport] = useState(false);

  useEffect(() => { fetchCollegeData(); }, [code]);

  const fetchCollegeData = async () => {
    try {
      setLoading(true);
      const collegeRes = await axios.get(`/colleges/code/${code}`);
      const collegeData = collegeRes.data.data || collegeRes.data;
      setCollege(collegeData);
      setLeadContext(collegeData.college_name, collegeData.college_code);
      const coursesRes = await axios.get('/courses', { params: { college_id: collegeData.id, limit: 100 } });
      const all = coursesRes.data.courses || [];
      setCourses(all.filter((c: Course) => c.is_active !== false));
      // Fetch similar colleges from same district
      if (collegeData.district?.id) {
        try {
          const simRes: any = await axios.get('/colleges', { params: { district_id: collegeData.district.id, limit: 8 } });
          // After interceptor: simRes = { success, data: { colleges: [...] } }
          const simAll = simRes.data?.colleges || [];
          setSimilarColleges(simAll.filter((c: any) => c.college_code !== collegeData.college_code).slice(0, 5));
        } catch {}
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load college');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrend = async (courseId: number, collegeId: number, community?: string) => {
    const comm = community ?? trendCommunity;
    setLoadingTrend(courseId);
    try {
      const res = await axios.get('/cutoffs/compare-colleges', {
        params: { colleges: String(collegeId), community: comm, start_year: CUTOFF_START_YEAR, end_year: CUTOFF_END_YEAR },
      });
      // After interceptor, res = { success, data: { comparison: [...] } }
      const comparison = (res as any).data?.comparison || [];
      const collegeTrends = comparison[0]?.courses || [];
      // Match by branch_code since the course ID from /courses may differ from compare API
      const thisCourse = courses.find(c => c.id === courseId);
      const trendCourse = thisCourse
        ? collegeTrends.find((c: any) => c.branch_code === thisCourse.branch_code)
        : null;
      const years = trendCourse?.years || [];
      setTrendData(prev => ({ ...prev, [courseId]: years }));
    } catch {
      setTrendData(prev => ({ ...prev, [courseId]: [] }));
    } finally {
      setLoadingTrend(null);
    }
  };

  const toggleCourse = async (courseId: number) => {
    if (expandedCourseId === courseId) { setExpandedCourseId(null); return; }
    setExpandedCourseId(courseId);
    const course = courses.find(c => c.id === courseId);
    if (course && !course.community_allocations) {
      setLoadingAllocations(courseId);
      try {
        const res: any = await axios.get('/community-allocations', { params: { course_id: courseId, year: ALLOTMENT_YEAR } });
        setCourses(prev => prev.map(c => c.id === courseId ? { ...c, community_allocations: res.allocations || [] } : c));
      } finally {
        setLoadingAllocations(null);
      }
    }
    if (college) fetchTrend(courseId, college.id);
  };

  const getMapUrl = () => {
    if (!college) return '';
    if (college.latitude && college.longitude) return `https://www.google.com/maps?q=${college.latitude},${college.longitude}`;
    return `https://www.google.com/maps/search/${encodeURIComponent(college.college_name)}`;
  };

  const shortName = (name: string) => {
    // Preserve Anna University campus identifier (CEG/MIT/ACT)
    const campusMatch = name.match(/^(.*?)\s*[-–]\s*([A-Z]{2,5}\s*Campus)/i);
    if (campusMatch) {
      const base = campusMatch[1].split(',')[0].trim();
      return `${base} — ${campusMatch[2]}`;
    }
    return name.length > 80 && name.includes(',') ? name.substring(0, name.indexOf(',')) : name;
  };

  const addressPart = (name: string) => {
    // If campus name is preserved, address is everything after the campus block
    const campusMatch = name.match(/^(.*?)\s*[-–]\s*[A-Z]{2,5}\s*Campus\s*,?\s*(.*)$/i);
    if (campusMatch) return campusMatch[2].trim() || null;
    return name.length > 80 && name.includes(',') ? name.substring(name.indexOf(',') + 1).trim() : null;
  };

  const scrollTo = (sectionId: string) => {
    setActiveSection(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navItems = [
    { id: 'overview', label: 'Overview' },
    { id: 'courses', label: `Courses (${courses.length})` },
    { id: 'facilities', label: 'Facilities' },
    { id: 'contact', label: 'Contact & Location' },
  ];

  if (loading) return (
    <div className="text-center py-20">
      <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
      <p className="text-gray-500 text-sm">Loading college details…</p>
    </div>
  );

  if (error || !college) return (
    <div className="max-w-2xl mx-auto mt-10 bg-red-50 border border-red-200 rounded-xl p-6">
      <p className="text-red-600 mb-3">{error || 'College not found'}</p>
      <Link to="/colleges" className="text-blue-600 text-sm hover:underline">← Back to Colleges</Link>
    </div>
  );

  const totalSeats = courses.reduce((s, c) => s + c.approved_intake_2025, 0);
  const hasHostel = college.boys_accommodation_available || college.girls_accommodation_available;
  const hasFees = college.annual_tuition_fee || college.development_fee;
  const isPrivate = college.college_type === 'Private';

  // For Private colleges, data is mixed monthly/annual — normalize to annual.
  // For all other types (Govt, Anna Univ), data is already annual per circular — use as-is.
  const toAnnual = (val: number | undefined | null, threshold: number): number | null => {
    if (val == null) return null;
    const n = Number(val);
    if (isNaN(n)) return null;
    return isPrivate ? (n <= threshold ? n * 12 : n) : n;
  };
  const hostelCost = (rent: number | undefined | null, elec: number | undefined | null) => {
    const r = toAnnual(rent, 6000);
    const e = toAnnual(elec, 2000);
    if (r == null && e == null) return null;
    return (r ?? 0) + (e ?? 0);
  };
  const hasHostelFees = college.admission_fees || college.caution_deposit || college.establishment_charges;

  return (
    <>
      <Helmet>
        <title>{`${college.college_name} TNEA ${CUTOFF_END_YEAR} Cutoff Marks & Closing Ranks — All Branches | ChooseMyCollege`}</title>
        <meta name="description" content={`${college.college_name} TNEA ${CUTOFF_END_YEAR} cutoff marks and closing ranks for all branches (CSE, ECE, Mech, Civil, IT) across OC, BC, BCM, MBC, SC, SCA, ST communities.${college.naac_grade ? ` NAAC ${college.naac_grade}.` : ''} Fees, hostel, placements.`} />
        <meta name="keywords" content={`${college.college_name} TNEA cutoff ${CUTOFF_END_YEAR}, ${college.college_name} closing rank, ${college.college_name} fees, ${college.college_name} ${college.district?.name}, Tamil Nadu engineering college ${CUTOFF_END_YEAR}`} />
        <meta property="og:title" content={`${college.college_name} TNEA ${CUTOFF_END_YEAR} Cutoff — All Branches | ChooseMyCollege`} />
        <meta property="og:description" content={`Complete TNEA ${CUTOFF_END_YEAR} cutoff data for ${college.college_name}, ${college.district?.name}. OC, BC, MBC, SC community-wise closing ranks for all branches.`} />
        <meta property="og:url" content={`https://choosemycollege.in/colleges/${college.college_code}`} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://choosemycollege.in/colleges/${college.college_code}`} />
      </Helmet>

      {courses.length > 0 && (
        <JsonLd
          data={[
            buildCollegeSchema(college, courses),
            buildCutoffDatasetSchema(college, courses, CUTOFF_END_YEAR),
            buildFAQSchema(college, courses.slice(0, 5), CUTOFF_END_YEAR),
            buildBreadcrumbSchema([
              { name: 'Home', url: 'https://choosemycollege.in/' },
              { name: 'Colleges', url: 'https://choosemycollege.in/colleges' },
              { name: college.district?.name || 'Tamil Nadu', url: `https://choosemycollege.in/colleges?district_id=${college.district?.id}` },
              { name: college.college_name, url: `https://choosemycollege.in/colleges/${college.college_code}` },
            ]),
          ]}
        />
      )}

      <div className="max-w-5xl mx-auto space-y-5">
        {/* Back */}
        <Link to="/colleges" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Colleges
        </Link>

        {/* Hero */}
        <div className="bg-gradient-to-br from-blue-700 to-indigo-700 rounded-2xl p-6 md:p-8 text-white">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="px-2.5 py-1 bg-white/20 text-white text-xs font-semibold rounded-full">Code: {college.college_code}</span>
                <span className="px-2.5 py-1 bg-white/20 text-white text-xs font-semibold rounded-full">
                  {college.college_type === 'Aided' ? 'Govt. Aided' : college.college_type}
                </span>
                {college.autonomous_status && <span className="px-2.5 py-1 bg-green-400/30 text-green-100 text-xs font-semibold rounded-full">Autonomous</span>}
                {college.naac_grade && <span className="px-2.5 py-1 bg-purple-400/30 text-purple-100 text-xs font-semibold rounded-full">NAAC {college.naac_grade}</span>}
                {college.womens_college && <span className="px-2.5 py-1 bg-pink-400/30 text-pink-100 text-xs font-semibold rounded-full">Women's College</span>}
                {college.minority_institution && <span className="px-2.5 py-1 bg-yellow-400/30 text-yellow-100 text-xs font-semibold rounded-full">Minority</span>}
              </div>
              <h1 className="text-xl md:text-2xl font-bold leading-snug mb-1">{shortName(college.college_name)}</h1>
              {addressPart(college.college_name) && (
                <p className="text-blue-200 text-sm">{addressPart(college.college_name)}</p>
              )}
              <p className="text-blue-200 text-sm mt-1 flex items-center gap-1">
                <FaMapMarkerAlt className="text-xs" />
                {college.district.name}{college.district.state ? `, ${college.district.state}` : ''}
              </p>
            </div>
            <a href={getMapUrl()} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition-colors text-sm shadow flex-shrink-0">
              📍 View on Map
            </a>
          </div>

          {/* Key stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-5 border-t border-white/20">
            <div className="bg-white/10 rounded-xl px-4 py-3 text-center">
              <div className="text-2xl font-bold">{courses.length}</div>
              <div className="text-blue-200 text-xs mt-0.5">Branches</div>
            </div>
            <div className="bg-white/10 rounded-xl px-4 py-3 text-center">
              <div className="text-2xl font-bold">{totalSeats}</div>
              <div className="text-blue-200 text-xs mt-0.5">Total Seats</div>
            </div>
            <div className="bg-white/10 rounded-xl px-4 py-3 text-center">
              <div className="text-2xl font-bold">{college.placement_info || '—'}</div>
              <div className="text-blue-200 text-xs mt-0.5">Placement %</div>
            </div>
            <div className="bg-white/10 rounded-xl px-4 py-3 text-center">
              <div className="text-2xl font-bold">{hasHostel ? '✓' : '✗'}</div>
              <div className="text-blue-200 text-xs mt-0.5">Hostel</div>
            </div>
          </div>
        </div>

        {/* Request Info Card */}
        <RequestInfoCard collegeName={college.college_name} collegeCode={college.college_code} />

        {/* Sticky section nav */}
        <div className="sticky top-[108px] z-30 bg-white/90 backdrop-blur-sm border border-gray-100 rounded-xl shadow-sm flex gap-1 p-1">
          {navItems.map(item => (
            <button key={item.id} onClick={() => scrollTo(item.id)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                activeSection === item.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}>
              {item.label}
            </button>
          ))}
        </div>

        {/* Quick Facts — SEO-visible keyword block, mirrors FAQ schema */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-4">
          <h2 className="text-base font-bold text-blue-900 mb-2">
            {college.college_name} TNEA {CUTOFF_END_YEAR} — Quick Facts
          </h2>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-sm text-gray-700">
            <li>📍 <span className="font-medium">{college.district?.name}</span>, Tamil Nadu</li>
            <li>🏛️ <span className="font-medium">{college.college_type}</span></li>
            {college.naac_grade && <li>🎓 NAAC <span className="font-medium">{college.naac_grade}</span></li>}
            <li>📚 <span className="font-medium">{courses.length}</span> branches offered</li>
            {college.annual_tuition_fee && (
              <li>💰 ₹<span className="font-medium">{Number(college.annual_tuition_fee).toLocaleString('en-IN')}</span>/yr tuition</li>
            )}
            <li>
              🏠{' '}
              {college.boys_accommodation_available && college.girls_accommodation_available
                ? <span className="font-medium">Boys & Girls hostel</span>
                : college.boys_accommodation_available
                ? <span className="font-medium">Boys hostel</span>
                : college.girls_accommodation_available
                ? <span className="font-medium">Girls hostel</span>
                : <span className="text-gray-400">No hostel</span>}
            </li>
            {college.transport_facilities && <li>🚌 <span className="font-medium">Transport available</span></li>}
            {college.autonomous_status && <li>✅ <span className="font-medium">Autonomous</span></li>}
          </ul>
        </div>

        {/* Overview — editorial prose derived from the college's own data */}
        <div ref={el => { sectionRefs.current['overview'] = el; }}>
          <Section id="overview" title="Overview">
            {(() => {
              const sentences = buildCollegeOverview(college, courses);
              const mid = Math.ceil(sentences.length / 2);
              const paragraphs = [sentences.slice(0, mid).join(' '), sentences.slice(mid).join(' ')].filter(Boolean);
              return (
                <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                  {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              );
            })()}
          </Section>
        </div>

        {/* Courses section */}
        <div ref={el => { sectionRefs.current['courses'] = el; }}>
          <Section id="courses" title={`Courses & Seat Allocation (${courses.length} branches)`}>
            {courses.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-6">No courses available</p>
            ) : (
              <div className="space-y-2">
                {courses.map(course => (
                  <div key={course.id} className="border border-gray-100 rounded-xl overflow-hidden">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleCourse(course.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleCourse(course.id); }}
                      className="w-full flex items-start justify-between p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex-1 min-w-0 mr-3">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-bold rounded flex-shrink-0">
                            {course.branch_code}
                          </span>
                          <span className="text-sm font-semibold text-gray-800">{course.branch_name}</span>
                          {course.nba_accredited && (
                            <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded">
                              NBA {course.accreditation_valid_upto ? `(till ${course.accreditation_valid_upto})` : '✓'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <span className="text-lg font-bold text-gray-800">{course.approved_intake_2025}</span>
                          <span className="text-xs text-gray-400 ml-1">seats</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addToChoiceList({
                              id: college.id, college_code: college.college_code,
                              college_name: college.college_name, college_type: college.college_type,
                              branch_code: course.branch_code, branch_name: course.branch_name,
                              naac_grade: college.naac_grade, district: college.district,
                              annual_tuition_fee: college.annual_tuition_fee,
                            });
                          }}
                          title={isInChoiceList(college.college_code, course.branch_code) ? 'In ChoiceList' : 'Add to ChoiceList'}
                          className={`p-1.5 rounded-full border transition-colors flex-shrink-0 ${
                            isInChoiceList(college.college_code, course.branch_code)
                              ? 'bg-green-100 border-green-300 text-green-700'
                              : 'bg-white border-gray-200 text-gray-400 hover:border-blue-400 hover:text-blue-600'
                          }`}
                        >
                          {isInChoiceList(college.college_code, course.branch_code) ? <FaCheck className="text-xs" /> : <FaHeart className="text-xs text-red-500" />}
                        </button>
                        {expandedCourseId === course.id
                          ? <FaChevronUp className="text-gray-400 text-xs" />
                          : <FaChevronDown className="text-gray-400 text-xs" />}
                      </div>
                    </div>

                    {/* Community allocation */}
                    {expandedCourseId === course.id && (
                      <div className="border-t border-gray-100 bg-gray-50 p-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                          Seats by Community ({ALLOTMENT_YEAR})
                        </p>
                        {loadingAllocations === course.id ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                          </div>
                        ) : course.community_allocations && course.community_allocations.length > 0 ? (
                          <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                            {course.community_allocations.map(alloc => {
                              const full = alloc.seats_allotted >= alloc.total_seats;
                              const half = alloc.seats_allotted >= alloc.total_seats * 0.6;
                              return (
                                <div key={alloc.id} className={`rounded-lg p-2.5 text-center border ${
                                  full ? 'bg-red-50 border-red-100' :
                                  half ? 'bg-orange-50 border-orange-100' :
                                  'bg-white border-gray-200'
                                }`}>
                                  <div className="text-xs font-bold text-gray-500 mb-1">{alloc.community}</div>
                                  <div className="text-base font-bold text-gray-800">
                                    {alloc.seats_allotted}
                                    <span className="text-gray-400 font-normal">/{alloc.total_seats}</span>
                                  </div>
                                  <div className={`text-xs font-semibold mt-0.5 ${
                                    full ? 'text-red-600' : half ? 'text-orange-500' : 'text-green-600'
                                  }`}>
                                    {full ? 'Full' : `${alloc.total_seats - alloc.seats_allotted} left`}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 text-center py-3">No seat data for {ALLOTMENT_YEAR}</p>
                        )}
                        {course.course_start_year && (
                          <p className="text-xs text-gray-400 mt-3">Course started: {course.course_start_year}</p>
                        )}

                        {/* Cutoff Trend Chart */}
                        <div className="mt-4 border-t border-gray-100 pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cutoff Trend ({CUTOFF_START_YEAR}–{CUTOFF_END_YEAR})</p>
                            <div className="flex gap-1">
                              {COMMUNITIES.map(c => (
                                <button
                                  key={c}
                                  onClick={() => {
                                    setTrendCommunity(c);
                                    setTrendData({});
                                    // Pass community directly — state update is async
                                    if (college) fetchTrend(course.id, college.id, c);
                                  }}
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                                    trendCommunity === c ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                  }`}
                                >
                                  {c}
                                </button>
                              ))}
                            </div>
                          </div>
                          {loadingTrend === course.id ? (
                            <div className="flex justify-center py-6">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                            </div>
                          ) : trendData[course.id] && trendData[course.id].length > 0 ? (
                            <ResponsiveContainer width="100%" height={180}>
                              <LineChart data={trendData[course.id]} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 10 }} width={35} domain={['auto', 'auto']} />
                                <Tooltip
                                  formatter={(val: any, name: string) => [val, name === 'cutoff_mark' ? 'Cutoff' : 'Closing Rank']}
                                  labelFormatter={(l) => `Year: ${l}`}
                                />
                                <Legend formatter={(val) => val === 'cutoff_mark' ? 'Cutoff Mark' : 'Closing Rank'} wrapperStyle={{ fontSize: 11 }} />
                                <Line type="monotone" dataKey="cutoff_mark" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                <Line type="monotone" dataKey="closing_rank" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} yAxisId={0} hide />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <p className="text-xs text-gray-400 text-center py-4">No historical cutoff data available</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* Facilities section */}
        <div ref={el => { sectionRefs.current['facilities'] = el; }}>
          <Section id="facilities" title="Facilities">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Hostel */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Hostel</h3>
                {(() => {
                  const b = college.boys_accommodation_available;
                  const g = college.girls_accommodation_available;
                  if (!b && !g) return <p className="text-sm text-gray-400">No hostel available</p>;

                  const messIcon = (type: string | undefined) => {
                    if (!type) return null;
                    if (type === 'Veg') return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">🌱 Veg</span>;
                    if (type === 'Non-Veg') return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">🍗 Non-Veg</span>;
                    return <span className="text-xs">{type}</span>;
                  };

                  const boysCost = hostelCost(college.boys_room_rent, college.boys_electricity_charges);
                  const girlsCost = hostelCost(college.girls_room_rent, college.girls_electricity_charges);
                  const sameCost = boysCost != null && girlsCost != null && boysCost === girlsCost;
                  const sameMess = college.boys_mess_type === college.girls_mess_type;
                  const sameType = college.boys_permanent_or_rental === college.girls_permanent_or_rental;
                  const merged = b && g && sameCost && sameMess && sameType;

                  // Shared hostel fee rows (not gender-specific)
                  const hostelFeeRows = hasHostelFees ? (
                    <>
                      {college.admission_fees != null && <InfoRow label="Hostel Admission Fee" value={`₹${Number(college.admission_fees).toLocaleString('en-IN')}`} />}
                      {college.caution_deposit != null && <InfoRow label="Caution Deposit" value={<span>₹{Number(college.caution_deposit).toLocaleString('en-IN')} <span className="text-xs text-gray-400">(refundable)</span></span>} />}
                      {college.establishment_charges != null && (
                        <InfoRow
                          label="Establishment Charges"
                          value={
                            <span>
                              ₹{Number(college.establishment_charges).toLocaleString('en-IN')}
                              {college.establishment_charges_period === 'semester' && <span className="ml-1 text-xs text-gray-400">/sem</span>}
                              {college.establishment_charges_period === 'annual' && <span className="ml-1 text-xs text-gray-400">/yr</span>}
                            </span>
                          }
                        />
                      )}
                    </>
                  ) : null;

                  if (merged) {
                    return (
                      <div className="rounded-xl border border-green-100 bg-green-50/40 p-4 space-y-1 text-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-700">🏠 Boys & Girls</span>
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Available</span>
                        </div>
                        {college.boys_permanent_or_rental && <InfoRow label="Type" value={college.boys_permanent_or_rental === 'P' ? 'Permanent' : 'Rental'} />}
                        {college.boys_mess_type && <InfoRow label="Mess" value={messIcon(college.boys_mess_type)} />}
                        {boysCost != null
                          ? <InfoRow label="Room + Electricity" value={<span>₹{boysCost.toLocaleString('en-IN')}/yr <span className="text-xs text-gray-400">(approx.)</span></span>} />
                          : <InfoRow label="Hostel Fee" value={<span className="text-xs text-amber-600 font-medium">Contact college for fee details</span>} />}
                        {hostelFeeRows}
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {b && (
                        <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 space-y-1 text-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-gray-700">👦 Boys</span>
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Available</span>
                          </div>
                          {college.boys_permanent_or_rental && <InfoRow label="Type" value={college.boys_permanent_or_rental === 'P' ? 'Permanent' : 'Rental'} />}
                          {college.boys_mess_type && <InfoRow label="Mess" value={messIcon(college.boys_mess_type)} />}
                          {boysCost != null
                            ? <InfoRow label="Room + Electricity" value={<span>₹{boysCost.toLocaleString('en-IN')}/yr <span className="text-xs text-gray-400">(approx.)</span></span>} />
                            : <InfoRow label="Hostel Fee" value={<span className="text-xs text-amber-600 font-medium">Contact college for fee details</span>} />}
                          {hostelFeeRows}
                        </div>
                      )}
                      {g && (
                        <div className="rounded-xl border border-pink-100 bg-pink-50/50 p-4 space-y-1 text-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-gray-700">👧 Girls</span>
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Available</span>
                          </div>
                          {college.girls_permanent_or_rental && <InfoRow label="Type" value={college.girls_permanent_or_rental === 'P' ? 'Permanent' : 'Rental'} />}
                          {college.girls_mess_type && <InfoRow label="Mess" value={messIcon(college.girls_mess_type)} />}
                          {girlsCost != null
                            ? <InfoRow label="Room + Electricity" value={<span>₹{girlsCost.toLocaleString('en-IN')}/yr <span className="text-xs text-gray-400">(approx.)</span></span>} />
                            : <InfoRow label="Hostel Fee" value={<span className="text-xs text-amber-600 font-medium">Contact college for fee details</span>} />}
                          {hostelFeeRows}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Fees, Transport & Calculator */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Fees</h3>
                  <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-1 text-sm">
                    {college.annual_tuition_fee != null ? (
                      <InfoRow
                        label="Annual Tuition Fee"
                        value={
                          <span>
                            ₹{Number(college.annual_tuition_fee).toLocaleString('en-IN')}/yr
                            <span className="ml-1.5 text-xs text-gray-400">(Govt quota · all-inclusive)</span>
                          </span>
                        }
                      />
                    ) : (
                      <InfoRow label="Annual Tuition Fee" value={<span className="text-xs text-amber-600 font-medium">Contact college for fee details</span>} />
                    )}
                    {college.development_fee != null && (
                      <InfoRow
                        label="Development Fee"
                        value={
                          <span>
                            ₹{Number(college.development_fee).toLocaleString('en-IN')}
                            <span className="ml-1.5 text-xs text-gray-400">(one-time)</span>
                          </span>
                        }
                      />
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Transport</h3>
                  <div className={`rounded-xl border p-4 ${college.transport_facilities ? 'border-green-100 bg-green-50/50' : 'border-gray-100 bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${college.transport_facilities ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className="text-sm font-semibold text-gray-700">
                        {college.transport_facilities ? 'Transport Available' : 'No Transport'}
                      </span>
                    </div>
                    {college.transport_facilities && (
                      <div className="text-sm space-y-1">
                        {college.min_transport_charges != null && <InfoRow label="Min Charges" value={`₹${college.min_transport_charges.toLocaleString()}/year`} />}
                        {college.max_transport_charges != null && <InfoRow label="Max Charges" value={`₹${college.max_transport_charges.toLocaleString()}/year`} />}
                      </div>
                    )}
                  </div>
                </div>

                {/* Fee Calculator */}
                {hasFees && (() => {
                  // Force numeric — API can return strings from DB
                  const tuition = Number(college.annual_tuition_fee) || 0;
                  const devFee = Number(college.development_fee) || 0;
                  const boysCost = hostelCost(college.boys_room_rent, college.boys_electricity_charges) || 0;
                  const girlsCost = hostelCost(college.girls_room_rent, college.girls_electricity_charges) || 0;
                  const hostelAnnual = feeHostel === 'boys' ? boysCost : feeHostel === 'girls' ? girlsCost : 0;
                  const estab = college.establishment_charges
                    ? (college.establishment_charges_period === 'semester'
                        ? Number(college.establishment_charges) * 2
                        : Number(college.establishment_charges))
                    : 0;
                  // Transport: average of min+max; fallback to whichever is available
                  const tMin = Number(college.min_transport_charges) || 0;
                  const tMax = Number(college.max_transport_charges) || 0;
                  const transportAnnual = feeTransport
                    ? (tMin > 0 && tMax > 0 ? (tMin + tMax) / 2 : tMin || tMax)
                    : 0;
                  const annualTotal = tuition + estab + hostelAnnual + transportAnnual;
                  const grandTotal = annualTotal * feeYears + devFee;
                  const fmt = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

                  return (
                    <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4">
                      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                        <FaCalculator className="text-blue-600" /> Fee Calculator
                      </h3>
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Years</span>
                          <div className="flex gap-1">
                            {[1,2,3,4].map(y => (
                              <button key={y} onClick={() => setFeeYears(y)}
                                className={`w-7 h-7 rounded text-xs font-bold transition-colors ${feeYears === y ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
                                {y}
                              </button>
                            ))}
                          </div>
                        </div>
                        {(college.boys_accommodation_available || college.girls_accommodation_available) && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Hostel</span>
                            <div className="flex gap-1">
                              {(['none', ...(college.boys_accommodation_available ? ['boys'] : []), ...(college.girls_accommodation_available ? ['girls'] : [])] as const).map((opt: any) => (
                                <button key={opt} onClick={() => setFeeHostel(opt)}
                                  className={`px-2 py-1 rounded text-xs font-medium transition-colors capitalize ${feeHostel === opt ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
                                  {opt === 'none' ? 'None' : opt === 'boys' ? 'Boys' : 'Girls'}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {college.transport_facilities && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Transport</span>
                            <button onClick={() => setFeeTransport(t => !t)}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${feeTransport ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
                              {feeTransport ? 'Yes' : 'No'}
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="border-t border-blue-100 pt-3 space-y-1">
                        {tuition > 0 && <div className="flex justify-between text-xs text-gray-600"><span>Tuition × {feeYears}</span><span>{fmt(tuition * feeYears)}</span></div>}
                        {devFee > 0 && <div className="flex justify-between text-xs text-gray-600"><span>Development fee (one-time)</span><span>{fmt(devFee)}</span></div>}
                        {hostelAnnual > 0 && <div className="flex justify-between text-xs text-gray-600"><span>Hostel × {feeYears}</span><span>{fmt(hostelAnnual * feeYears)}</span></div>}
                        {estab > 0 && <div className="flex justify-between text-xs text-gray-600"><span>Establishment × {feeYears}</span><span>{fmt(estab * feeYears)}</span></div>}
                        {transportAnnual > 0 && <div className="flex justify-between text-xs text-gray-600"><span>Transport × {feeYears} {tMin > 0 && tMax > 0 ? <span className="text-gray-400">(avg)</span> : ''}</span><span>{fmt(transportAnnual * feeYears)}</span></div>}
                        <div className="flex justify-between text-sm font-bold text-gray-800 pt-1 border-t border-blue-200">
                          <span>Estimated {feeYears}-year total</span>
                          <span className="text-blue-700">{fmt(grandTotal)}</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-2">Approximate estimate. Actual fees may vary.</p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </Section>
        </div>

        {/* Contact & Location section */}
        <div ref={el => { sectionRefs.current['contact'] = el; }}>
          <Section id="contact" title="Contact & Location">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                {college.address && (
                  <div className="flex gap-3">
                    <FaMapMarkerAlt className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Address</p>
                      <p className="text-sm text-gray-700">{college.address}{college.pincode ? ` — ${college.pincode}` : ''}</p>
                    </div>
                  </div>
                )}
                {college.phone && (
                  <div className="flex gap-3 items-center">
                    <FaPhoneAlt className="text-blue-500 flex-shrink-0" />
                    <a href={`tel:${college.phone}`} className="text-sm text-blue-600 hover:underline">{college.phone}</a>
                  </div>
                )}
                {college.email && (
                  <div className="flex gap-3 items-center">
                    <FaEnvelope className="text-blue-500 flex-shrink-0" />
                    <a href={`mailto:${college.email}`} className="text-sm text-blue-600 hover:underline truncate">{college.email}</a>
                  </div>
                )}
                {college.website && (
                  <div className="flex gap-3 items-center">
                    <FaGlobe className="text-blue-500 flex-shrink-0" />
                    <a href={college.website.startsWith('http') ? college.website : `https://${college.website}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline truncate">{college.website}</a>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {(college.distance_from_district_hq || college.nearest_railway_station || college.distance_from_railway) && (
                  <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Accessibility</p>
                    {college.distance_from_district_hq && (
                      <InfoRow label="Distance from District HQ" value={`${college.distance_from_district_hq} km`} />
                    )}
                    {college.nearest_railway_station && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-500 flex items-center gap-1.5"><FaTrain className="text-gray-400" /> Nearest Station</span>
                        <span className="text-sm font-semibold text-gray-800">{college.nearest_railway_station}</span>
                      </div>
                    )}
                    {college.distance_from_railway && (
                      <InfoRow label="Distance from Station" value={`${college.distance_from_railway} km`} />
                    )}
                  </div>
                )}
                <a href={getMapUrl()} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors text-sm">
                  📍 Open in Google Maps
                </a>
              </div>
            </div>
          </Section>
        </div>
        {/* Similar Colleges */}
        {similarColleges.length > 0 && (
          <Section id="similar" title={`More Colleges in ${college.district.name}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {similarColleges.map(sim => {
                const simShort = sim.college_name.length > 60 && sim.college_name.includes(',')
                  ? sim.college_name.substring(0, sim.college_name.indexOf(','))
                  : sim.college_name;
                return (
                  <Link
                    key={sim.id}
                    to={`/colleges/${sim.college_code}`}
                    className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 group-hover:text-blue-700 leading-snug">{simShort}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        <span className="text-xs text-gray-400">{sim.college_type}</span>
                        {sim.naac_grade && <span className="text-xs font-medium text-purple-600">NAAC {sim.naac_grade}</span>}
                        {sim.annual_tuition_fee && <span className="text-xs text-gray-400">₹{Number(sim.annual_tuition_fee).toLocaleString('en-IN')}/yr</span>}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <Link to={`/colleges?district_id=${college.district.id}`} className="inline-block mt-4 text-xs text-blue-600 hover:underline">
              View all colleges in {college.district.name} →
            </Link>
          </Section>
        )}

        {/* FAQ — visible counterpart of FAQPage schema; helps Google trust the data */}
        <Section id="faq" title={`Frequently Asked Questions — ${college.college_name}`}>
          <div className="space-y-2">
            {[
              {
                q: `What is ${college.college_name} TNEA ${CUTOFF_END_YEAR} cutoff?`,
                a: `The TNEA ${CUTOFF_END_YEAR} cutoff for ${college.college_name} varies by branch and community. See the Courses & Seat Allocation section above for branch-wise closing ranks across OC, BC, BCM, MBC, SC, SCA, and ST categories.`,
              },
              {
                q: `Where is ${college.college_name} located?`,
                a: `${college.college_name} is located in ${college.district?.name}, Tamil Nadu. ${college.address || ''}`.trim(),
              },
              {
                q: `What is the NAAC grade of ${college.college_name}?`,
                a: college.naac_grade
                  ? `${college.college_name} holds a NAAC grade of ${college.naac_grade}.`
                  : `${college.college_name} does not currently have a listed NAAC accreditation grade.`,
              },
              {
                q: `Is ${college.college_name} a government or private college?`,
                a: `${college.college_name} is a ${college.college_type} engineering college affiliated to Anna University, Tamil Nadu.`,
              },
              {
                q: `Does ${college.college_name} provide hostel facilities?`,
                a: college.boys_accommodation_available || college.girls_accommodation_available
                  ? `${college.college_name} provides ${[college.boys_accommodation_available && 'boys hostel', college.girls_accommodation_available && 'girls hostel'].filter(Boolean).join(' and ')}.`
                  : `${college.college_name} does not have on-campus hostel facilities. Please contact the college for nearby accommodation options.`,
              },
            ].map(({ q, a }, i) => (
              <details key={i} className="group border border-gray-100 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between gap-3 p-4 cursor-pointer font-semibold text-sm text-gray-800 hover:bg-gray-50 transition-colors list-none">
                  {q}
                  <span className="flex-shrink-0 text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="px-4 pb-4 pt-1 text-sm text-gray-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </Section>
      </div>
    </>
  );
};

export default CollegeDetails;
